import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaDownload, FaSyncAlt } from 'react-icons/fa';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PaginationBar from '@/components/PaginationBar';
import SortHeader, { type SortDirection } from '@/components/SortHeader';
import { formatBytes, formatCount, formatDate, formatNullableBool } from './format';
import type {
  ExtensionSummaryDto,
  FileInventoryDataSource,
  FileInventoryItemDto,
  FileInventoryQuery,
  FilteredFileSummaryDto,
  PagedResultDto,
  SiteScope,
} from '@/api/sharepointInventoryApi';

const PAGE_SIZE = 50;

interface Props {
  /** Must be referentially stable (memoize it), otherwise every render refetches. */
  dataSource: FileInventoryDataSource;
  siteScope?: SiteScope;
  siteCollectionId?: number;
  listId?: string;
  /** Extra leading filter columns, e.g. the admin scope and site pickers. */
  scopeControls?: React.ReactNode;
  showSiteColumn?: boolean;
  siteLink?: (file: FileInventoryItemDto) => string;
  exportFileNamePrefix?: string;
}

/** Filterable file list, filter summary and CSV export shared by the admin inventory and My Resources. */
const FileInventoryExplorer: React.FC<Props> = ({
  dataSource,
  siteScope,
  siteCollectionId,
  listId,
  scopeControls,
  showSiteColumn = true,
  siteLink,
  exportFileNamePrefix = 'sharepoint-files',
}) => {
  const [search, setSearch] = useState('');
  const [extensions, setExtensions] = useState<string[]>([]);
  const [notModifiedSince, setNotModifiedSince] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [minSizeMb, setMinSizeMb] = useState('');
  const [filterVersion, setFilterVersion] = useState(0);

  const [extensionOptions, setExtensionOptions] = useState<ExtensionSummaryDto[]>([]);
  const [extensionSort, setExtensionSort] = useState<'size' | 'count' | 'name'>('name');
  const [extensionFilter, setExtensionFilter] = useState('');
  const [extensionMinCount, setExtensionMinCount] = useState(2);

  const [data, setData] = useState<PagedResultDto<FileInventoryItemDto> | null>(null);
  const [summary, setSummary] = useState<FilteredFileSummaryDto | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Page is keyed to the scope so changing site/scope lands on page 1 without an extra fetch of the old page.
  const scopeKey = `${siteScope ?? ''}|${siteCollectionId ?? ''}|${listId ?? ''}|${filterVersion}`;
  const [pageState, setPageState] = useState({ key: scopeKey, page: 1 });
  const page = pageState.key === scopeKey ? pageState.page : 1;
  const setPage = useCallback((next: number) => setPageState({ key: scopeKey, page: next }), [scopeKey]);

  const [sortBy, setSortBy] = useState('fileSize');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const dataRefreshing = loading || summaryLoading;

  // Fetched once per site scope: sorting and filtering happen locally so no option is ever truncated away.
  useEffect(() => {
    dataSource
      .getExtensions({ siteScope, siteCollectionId })
      .then(setExtensionOptions)
      .catch(() => setExtensionOptions([]));
  }, [dataSource, siteCollectionId, siteScope]);

  const visibleExtensions = useMemo(() => {
    const needle = extensionFilter.trim().toLowerCase();
    const aboveThreshold = extensionOptions.filter((e) => e.fileCount >= extensionMinCount);
    const filtered = needle
      ? aboveThreshold.filter((e) => (e.fileExtension || '(none)').includes(needle))
      : aboveThreshold;

    const sorted = [...filtered];
    if (extensionSort === 'name') {
      sorted.sort((a, b) => (a.fileExtension || '(none)').localeCompare(b.fileExtension || '(none)'));
    } else if (extensionSort === 'count') {
      sorted.sort((a, b) => b.fileCount - a.fileCount);
    } else {
      sorted.sort((a, b) => b.totalBytes - a.totalBytes);
    }
    return sorted;
  }, [extensionOptions, extensionFilter, extensionSort, extensionMinCount]);

  const query = useMemo<FileInventoryQuery>(
    () => ({
      siteScope,
      siteCollectionId,
      listId,
      extensions: extensions.length ? extensions.join(',') : undefined,
      notModifiedSince: notModifiedSince || undefined,
      createdBefore: createdBefore || undefined,
      minSizeBytes: minSizeMb ? Math.round(Number(minSizeMb) * 1024 * 1024) : undefined,
      search: search.trim() || undefined,
    }),
    // Filter edits are held until Apply bumps filterVersion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteCollectionId, listId, filterVersion, siteScope]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await dataSource.getFiles({
          ...query,
          skip: (page - 1) * PAGE_SIZE,
          take: PAGE_SIZE,
          sortBy,
          sortDirection,
        })
      );
      setError(null);
    } catch {
      setError('Failed to load files.');
    } finally {
      setLoading(false);
    }
  }, [dataSource, query, page, sortBy, sortDirection]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    dataSource
      .getFilesSummary(query)
      .then((result) => {
        if (!cancelled) setSummary(result);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dataSource, query, refreshKey]);

  const onSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const applyFilters = (ev: React.FormEvent) => {
    ev.preventDefault();
    setFilterVersion((v) => v + 1);
  };

  const clearFilters = () => {
    setSearch('');
    setExtensions([]);
    setNotModifiedSince('');
    setCreatedBefore('');
    setMinSizeMb('');
    setFilterVersion((v) => v + 1);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await dataSource.exportFiles({ ...query, sortBy, sortDirection });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportFileNamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const toggleExtension = (ext: string) => {
    setExtensions((current) =>
      current.includes(ext) ? current.filter((e) => e !== ext) : [...current, ext]
    );
  };

  const hasScopeControls = Boolean(scopeControls);
  const minSizeControl = (
    <>
      <Form.Label className="small mb-1">Min size (MB)</Form.Label>
      <Form.Control type="number" min={0} value={minSizeMb} onChange={(e) => setMinSizeMb(e.target.value)} />
    </>
  );
  const columnCount = showSiteColumn ? 8 : 7;

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={applyFilters}>
            <Row className="g-2">
              {scopeControls}
              <Col md={hasScopeControls ? 3 : 4}>
                <Form.Label className="small mb-1">File name contains</Form.Label>
                <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} />
              </Col>
              <Col md={hasScopeControls ? 2 : 3}>
                <Form.Label className="small mb-1">Not modified since</Form.Label>
                <Form.Control type="date" value={notModifiedSince} onChange={(e) => setNotModifiedSince(e.target.value)} />
              </Col>
              <Col md={hasScopeControls ? 2 : 3}>
                <Form.Label className="small mb-1">Created before</Form.Label>
                <Form.Control type="date" value={createdBefore} onChange={(e) => setCreatedBefore(e.target.value)} />
              </Col>
              {!hasScopeControls && <Col md={2}>{minSizeControl}</Col>}
            </Row>

            {hasScopeControls && (
              <Row className="g-2 mt-0">
                <Col md={2} />
                <Col md={3}>{minSizeControl}</Col>
              </Row>
            )}

            {extensionOptions.length > 0 && (
              <div className="mt-3">
                <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                  <div className="small text-muted">Extensions</div>
                  <Form.Control
                    size="sm"
                    style={{ maxWidth: 180 }}
                    placeholder="Find extension"
                    value={extensionFilter}
                    onChange={(e) => setExtensionFilter(e.target.value)}
                  />
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 190 }}
                    value={extensionSort}
                    onChange={(e) => setExtensionSort(e.target.value as 'size' | 'count' | 'name')}
                  >
                    <option value="name">Sort: A-Z</option>
                    <option value="count">Sort: most files</option>
                    <option value="size">Sort: largest storage</option>
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 210 }}
                    value={extensionMinCount}
                    onChange={(e) => setExtensionMinCount(Number(e.target.value))}
                    title="Extensions seen only once or twice are usually stray file names, not real formats"
                  >
                    <option value={1}>Show all extensions</option>
                    <option value={2}>Appearing more than once</option>
                    <option value={6}>Appearing more than 5 times</option>
                  </Form.Select>
                  <span className="small text-muted">
                    {visibleExtensions.length === extensionOptions.length
                      ? `${formatCount(extensionOptions.length)} types`
                      : `${formatCount(visibleExtensions.length)} of ${formatCount(extensionOptions.length)} types`}
                  </span>
                  {extensions.length > 0 && (
                    <Button variant="link" size="sm" className="p-0" onClick={() => setExtensions([])}>
                      Clear {extensions.length} selected
                    </Button>
                  )}
                </div>

                {extensions.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {extensions.map((value) => (
                      <Badge
                        key={value}
                        bg="primary"
                        role="button"
                        onClick={() => toggleExtension(value)}
                        title="Remove"
                      >
                        {value} &times;
                      </Badge>
                    ))}
                  </div>
                )}

                <div
                  className="d-flex flex-wrap gap-3 border rounded p-2"
                  style={{ maxHeight: 160, overflowY: 'auto' }}
                >
                  {visibleExtensions.map((e) => {
                    const value = e.fileExtension || '(none)';
                    return (
                      <Form.Check
                        key={value}
                        type="checkbox"
                        id={`ext-${value}`}
                        label={`${value} (${formatCount(e.fileCount)})`}
                        checked={extensions.includes(value)}
                        onChange={() => toggleExtension(value)}
                      />
                    );
                  })}
                  {visibleExtensions.length === 0 && (
                    <span className="text-muted small">No extensions match &ldquo;{extensionFilter}&rdquo;.</span>
                  )}
                </div>
              </div>
            )}

            <div className="d-flex gap-2 mt-3">
              <Button type="submit" variant="primary">Apply filters</Button>
              <Button variant="outline-secondary" onClick={clearFilters}>Clear</Button>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setRefreshKey((k) => k + 1);
                  load();
                }}
                disabled={loading}
                title="Reload results using the filters already applied"
              >
                <FaSyncAlt className="me-1" />Refresh
              </Button>
              <Button variant="outline-secondary" onClick={onExport} disabled={exporting} title="Exports up to 100,000 rows">
                {exporting ? <Spinner size="sm" animation="border" /> : <><FaDownload className="me-1" />Export CSV</>}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <div className="position-relative">
        {dataRefreshing && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5"
            style={{ zIndex: 2, backgroundColor: 'rgba(255, 255, 255, 0.65)', minHeight: 420 }}
          >
            <div className="d-flex align-items-center gap-2 bg-white border rounded px-3 py-2 shadow-sm">
              <Spinner animation="border" size="sm" />
              <span>Refreshing inventory data...</span>
            </div>
          </div>
        )}
        <div style={{ opacity: dataRefreshing ? 0.45 : 1, transition: 'opacity 150ms ease-in-out' }}>
          <Accordion defaultActiveKey="summary" className="mb-3">
            <Accordion.Item eventKey="summary">
              <Accordion.Header>Summary of current filter</Accordion.Header>
              <Accordion.Body>
                {summaryLoading && !summary ? (
                  <Spinner animation="border" />
                ) : !summary ? (
                  <div className="text-muted">No summary available.</div>
                ) : (
                  <>
                    <Row className="g-3 mb-3">
                      <Col md={3}>
                        <Card body>
                          <div className="text-muted small">Files matched</div>
                          <div className="fs-4">{formatCount(summary.fileCount)}</div>
                          <div className="text-muted small">
                            {showSiteColumn && `${formatCount(summary.siteCount)} sites, `}
                            {formatCount(summary.libraryCount)} libraries
                          </div>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card body>
                          <div className="text-muted small">Storage matched</div>
                          <div className="fs-4">{formatBytes(summary.totalBytes)}</div>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card body>
                          <div className="text-muted small">Not modified in 2+ years</div>
                          <div className="fs-4">{formatCount(summary.notModifiedSince2yCount)}</div>
                          <div className="text-muted small">{formatBytes(summary.notModifiedSince2yBytes)}</div>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card body>
                          <div className="text-muted small">Modified range</div>
                          <div className="fs-6">{formatDate(summary.oldestModifiedDt)}</div>
                          <div className="fs-6">to {formatDate(summary.newestModifiedDt)}</div>
                        </Card>
                      </Col>
                    </Row>

                    <Row className="g-3">
                      <Col lg={7}>
                        <Card>
                          <Card.Header className="py-2 small">Largest extensions in this filter</Card.Header>
                          <Card.Body style={{ height: 260 }}>
                            {summary.topExtensions.length === 0 ? (
                              <div className="text-muted">No matching files.</div>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={summary.topExtensions.map((e) => ({
                                    name: e.fileExtension || '(none)',
                                    bytes: e.totalBytes,
                                  }))}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis tickFormatter={(v) => formatBytes(Number(v))} width={80} />
                                  <Tooltip formatter={(value) => formatBytes(Number(value))} />
                                  <Bar dataKey="bytes" fill="#0d6efd" name="Storage" />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col lg={5}>
                        <Card>
                          <Card.Header className="py-2 small">Time since last modified</Card.Header>
                          <Card.Body style={{ height: 260 }}>
                            {summary.ageBuckets.length === 0 ? (
                              <div className="text-muted">No matching files.</div>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={summary.ageBuckets.map((b) => ({ name: b.bucket, bytes: b.totalBytes }))}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis tickFormatter={(v) => formatBytes(Number(v))} width={80} />
                                  <Tooltip formatter={(value) => formatBytes(Number(value))} />
                                  <Bar dataKey="bytes" name="Storage">
                                    {summary.ageBuckets.map((b) => (
                                      <Cell key={b.bucket} fill={b.sortOrder >= 4 && b.sortOrder <= 6 ? '#fd7e14' : '#0d6efd'} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <Card>
            <Card.Body>
              {loading && !data ? (
                <Spinner animation="border" />
              ) : (
                <>
                  <Table hover size="sm" responsive>
                    <thead>
                      <tr>
                        <th><SortHeader label="File" column="fileName" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                        <th><SortHeader label="Ext" column="fileExtension" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                        {showSiteColumn && (
                          <th><SortHeader label="Site" column="siteTitle" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                        )}
                        <th>Library</th>
                        <th className="text-end"><SortHeader label="Size" column="fileSize" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                        <th><SortHeader label="Modified" column="modifiedDt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                        <th><SortHeader label="Created" column="createdDt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                        <th>Unique perms</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.items.map((f) => (
                        <tr key={f.sharePointFileId}>
                          <td>
                            <div>{f.fileName}</div>
                            <div className="text-muted small text-truncate" style={{ maxWidth: 420 }}>{f.serverRelativeUrl}</div>
                          </td>
                          <td>{f.fileExtension || '(none)'}</td>
                          {showSiteColumn && (
                            <td>{siteLink ? <Link to={siteLink(f)}>{f.siteTitle}</Link> : f.siteTitle}</td>
                          )}
                          <td>{f.libraryTitle ?? 'Unknown'}</td>
                          <td className="text-end">{formatBytes(f.fileSize)}</td>
                          <td>{formatDate(f.modifiedDt)}</td>
                          <td>{formatDate(f.createdDt)}</td>
                          <td>{formatNullableBool(f.hasUniquePermissions)}</td>
                        </tr>
                      ))}
                      {data?.items.length === 0 && (
                        <tr><td colSpan={columnCount} className="text-muted">No files match these filters.</td></tr>
                      )}
                    </tbody>
                  </Table>
                  {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default FileInventoryExplorer;
